import { useEffect, useState } from 'react';
import { useCanvasStore } from '../store/canvas-store';
import { Blob } from '../render/Blob';
import { Eye } from 'lucide-react';
import { usePresenceCtx } from './PresenceProvider';
import { useSyncStatus } from './useSyncStatus';
import { useReadOnly } from '../chrome/readonly';
import type { PresencePeer } from './types';

const MAX_VISIBLE = 6;

/** Dedupe peers by identity (multiple tabs of one user collapse to one face). */
function dedupe(peers: PresencePeer[]): PresencePeer[] {
  const seen = new Map<string, PresencePeer>();
  for (const p of peers) if (!seen.has(p.id)) seen.set(p.id, p);
  return [...seen.values()];
}

/**
 * Identity faces for everyone else on the canvas, stacked vertically under the
 * account blob (top-left). Click a face to FOLLOW that peer's viewport; Esc
 * exits. A connection pill sits at the foot of the stack.
 */
export function Facepile() {
  const { others } = usePresenceCtx();
  const dispatch = useCanvasStore((s) => s.dispatch);
  const status = useSyncStatus();
  const readOnly = useReadOnly();
  const [followId, setFollowId] = useState<string | null>(null);

  const peers = dedupe(others);
  const followed = peers.find((p) => p.id === followId) ?? null;

  // While following, mirror the followed peer's viewport locally.
  useEffect(() => {
    if (!followed?.viewport) return;
    dispatch({ t: 'viewport.set', pan: followed.viewport.pan, zoom: followed.viewport.zoom });
  }, [followed?.viewport?.pan.x, followed?.viewport?.pan.y, followed?.viewport?.zoom, followed, dispatch]);

  // Esc exits follow mode.
  useEffect(() => {
    if (!followId) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setFollowId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [followId]);

  // Drop follow if the peer leaves.
  useEffect(() => { if (followId && !followed) setFollowId(null); }, [followId, followed]);

  const visible = peers.slice(0, MAX_VISIBLE);
  const overflow = peers.length - visible.length;
  const showPill = status !== null;

  if (peers.length === 0 && !showPill && !readOnly) return null;

  return (
    <div className="fixed left-4 top-[68px] z-[60] flex flex-col items-start gap-2">
      {readOnly && (
        <span
          className="flex items-center gap-1 whitespace-nowrap rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-[10px] font-medium text-[var(--color-text-muted)] shadow-[var(--shadow-1)]"
          title="You have view-only access to this project"
          aria-label="View-only access"
        >
          <Eye size={11} /> view-only
        </span>
      )}
      {/* Overlapping vertical stack (§22): each face laps the one above it. */}
      {visible.length > 0 && (
        <div className="flex flex-col items-start pl-1">
          {visible.map((p, i) => (
            <FaceButton
              key={p.id}
              peer={p}
              first={i === 0}
              following={p.id === followId}
              onClick={() => setFollowId((cur) => (cur === p.id ? null : p.id))}
            />
          ))}
          {overflow > 0 && (
            <span
              className="grid h-[34px] w-[34px] -mt-2 place-items-center rounded-full border-2 border-[var(--color-surface)] bg-[var(--color-surface-2)] text-[12px] font-semibold text-[var(--color-text-muted)] shadow-[0_2px_6px_-2px_rgba(0,0,0,.35)]"
              title={`${overflow} more`}
              aria-label={`${overflow} more participants`}
            >
              +{overflow}
            </span>
          )}
        </div>
      )}
      {showPill && <ConnectionPill status={status} />}
    </div>
  );
}

function FaceButton({ peer, first, following, onClick }: { peer: PresencePeer; first: boolean; following: boolean; onClick: () => void }) {
  // 34px circle, mono initials, 2px surface ring; overlaps the face above.
  const ring = following
    ? { boxShadow: `0 0 0 2px var(--color-surface), 0 0 0 4px ${peer.color}` }
    : undefined;
  return (
    <button
      onClick={onClick}
      aria-label={`${peer.name}${following ? ' — following (Esc to exit)' : ' — click to follow'}`}
      aria-pressed={following}
      title={following ? `Following ${peer.name} — Esc to exit` : `Follow ${peer.name}`}
      className={`relative block cursor-pointer rounded-full transition-transform hover:z-10 hover:scale-110 active:scale-95 focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] ${first ? '' : '-mt-2'}`}
    >
      {peer.isAgent ? (
        <span
          className="grid h-[34px] w-[34px] place-items-center rounded-full border-2 border-[var(--color-surface)] bg-[var(--color-surface)] shadow-[0_2px_6px_-2px_rgba(0,0,0,.35)]"
          style={ring}
        >
          <Blob size={20} />
        </span>
      ) : (
        <span
          className="grid h-[34px] w-[34px] place-items-center rounded-full border-2 border-[var(--color-surface)] font-mono text-[13px] font-semibold text-white shadow-[0_2px_6px_-2px_rgba(0,0,0,.35)]"
          style={{ background: peer.color, ...ring }}
        >
          {peer.initials}
        </span>
      )}
    </button>
  );
}

function ConnectionPill({ status }: { status: 'connecting' | 'connected' | 'disconnected' }) {
  const map = {
    connected: { label: 'live · wss', color: 'var(--color-accent-2)', blink: false },
    connecting: { label: 'reconnecting…', color: 'var(--color-warning)', blink: true },
    disconnected: { label: 'offline', color: 'var(--color-danger)', blink: false },
  } as const;
  const { label, color, blink } = map[status];
  return (
    <span
      className="flex items-center gap-2 whitespace-nowrap rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[13px] text-[var(--color-text-muted)] shadow-[var(--shadow-1)]"
      role="status"
      aria-live="polite"
      aria-label={`Sync status: ${label}`}
    >
      <span
        data-anim={blink ? '' : undefined}
        className="h-2 w-2 rounded-full"
        style={{ background: color, boxShadow: `0 0 0 3px color-mix(in oklab, ${color} 25%, transparent)`, animation: blink ? 'cf-blink 1.1s var(--ease-out, ease) infinite' : undefined }}
      />
      {label}
    </span>
  );
}
